import type { TypedSupabaseClient } from '../client';
import type {
  IProArticlePricingRepository,
  ProArticlePricingRow,
  UpsertProArticlePricingInput,
} from '../../../interfaces/src';

const PRICING_FETCH_BATCH_SIZE = 1000;

export class SupabaseProArticlePricingRepository implements IProArticlePricingRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async listForArticles(articleIds: string[]): Promise<ProArticlePricingRow[]> {
    if (articleIds.length === 0) return [];
    const { data, error } = await (this.supabase as any)
      .from('pro_article_pricing')
      .select('*')
      .in('article_id', articleIds);
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapPricing);
  }

  async listAll(): Promise<ProArticlePricingRow[]> {
    const rows: Array<Record<string, unknown>> = [];
    let offset = 0;
    let total: number | null = null;

    do {
      const { data, error, count } = await (this.supabase as any)
        .from('pro_article_pricing')
        .select('*', { count: 'exact' })
        .range(offset, offset + PRICING_FETCH_BATCH_SIZE - 1);
      if (error) throw error;

      const batch = (data ?? []) as Array<Record<string, unknown>>;
      rows.push(...batch);
      total = typeof count === 'number' ? count : total;
      if (batch.length < PRICING_FETCH_BATCH_SIZE) break;
      offset += PRICING_FETCH_BATCH_SIZE;
    } while (total === null || offset < total);

    return rows.map(mapPricing);
  }

  async upsert(input: UpsertProArticlePricingInput): Promise<ProArticlePricingRow> {
    const { data, error } = await (this.supabase as any)
      .from('pro_article_pricing')
      .upsert({
        article_id: input.articleId,
        prix_pro_min: input.prixProMin ?? null,
        prix_pro_moy: input.prixProMoy ?? null,
        prix_pro_max: input.prixProMax ?? null,
        remise_min: input.remiseMin ?? 0,
        remise_moy: input.remiseMoy ?? 0,
        remise_max: input.remiseMax ?? 0,
      })
      .select()
      .single();
    if (error) throw error;
    return mapPricing(data as Record<string, unknown>);
  }

  async delete(articleId: string): Promise<void> {
    const { error } = await (this.supabase as any)
      .from('pro_article_pricing')
      .delete()
      .eq('article_id', articleId);
    if (error) throw error;
  }
}

function mapPricing(row: Record<string, unknown>): ProArticlePricingRow {
  return {
    articleId: row['article_id'] as string,
    prixProMin: row['prix_pro_min'] != null ? Number(row['prix_pro_min']) : null,
    prixProMoy: row['prix_pro_moy'] != null ? Number(row['prix_pro_moy']) : null,
    prixProMax: row['prix_pro_max'] != null ? Number(row['prix_pro_max']) : null,
    remiseMin: Number(row['remise_min'] ?? 0),
    remiseMoy: Number(row['remise_moy'] ?? 0),
    remiseMax: Number(row['remise_max'] ?? 0),
    updatedAt: row['updated_at'] as string,
  };
}
